import { useMemo } from 'react';

import {
  Inline,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  withTooltip,
} from '@crossnokaye/ux-common';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crossnokaye/ux-common/ckds';

import {
  ButtonComponentActionConfigVariant,
  DropdownComponentActionConfig,
  DropdownOption,
} from '@repo/sdk/models/components';

import {
  DropdownButton,
  DropdownButtonProps,
} from '@/ux-common/components/dropdown-button/dropdown-button';
import { cn } from '@/ux-common/lib/utils';

import { ActionIcon } from './action-icon';
import { mapLegacyButtonVariantToClassName } from '../utils';

const DropdownButtonWithTooltip = withTooltip(DropdownButton);

/**
 * Display config inherited from SDK type, minus valueRef and confirmationDialogConfig
 */
type DropdownDisplayConfig = Omit<
  DropdownComponentActionConfig,
  'valueRef' | 'confirmationDialogConfig'
>;

/**
 * ButtonProps without conflicting properties (onSelect, value)
 */
type BaseButtonProps = Omit<DropdownButtonProps, 'onSelect' | 'value'>;

export interface ActionDropdownDisplayProps extends BaseButtonProps, DropdownDisplayConfig {
  /** Whether to disable the items */
  disableItems?: boolean;
  /** Selection handler called when user selects an option */
  onSelect: (option: DropdownOption) => void;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional state name for data attribute */
  stateName?: string;
  /** Optional tooltip content shown on button hover */
  tooltipContent?: string;
  /** Explicit controlled value (replaces valueRef from config) */
  value: string | undefined;
}

/**
 * Presentational component for action dropdown display.
 * Renders a dropdown menu with options, handling display of current value and selection.
 * This is a controlled component - value and selection are managed externally.
 */
export const ActionDropdownDisplay = ({
  buttonVariant = ButtonComponentActionConfigVariant.Default,
  disabled, // Disable the button
  disableItems, // Disable the dropdown items
  options = [],
  showIcon,
  subtitle,
  onSelect,
  placeholder,
  stateName,
  tooltipContent,
  value,
  className,
  ...props
}: ActionDropdownDisplayProps) => {
  const currentOption = useMemo(() => {
    return options.find((option: DropdownOption) => option.value === value);
  }, [options, value]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-state-name={stateName}
        asChild
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className="w-full"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DropdownButtonWithTooltip
            className={cn(
              'btn-sm w-full [&>span:first-child]:min-w-0 [&>span:first-child]:overflow-hidden',
              mapLegacyButtonVariantToClassName(
                buttonVariant as ButtonComponentActionConfigVariant
              ),
              className
            )}
            delayDuration={0}
            tooltipContent={tooltipContent}
            {...props}
          >
            <Inline className="flex flex-row gap-x-2 min-w-0 overflow-hidden">
              {showIcon && currentOption?.icon?.icon && (
                <ActionIcon
                  icon={currentOption?.icon?.icon}
                  variant={currentOption?.icon?.variant}
                  fallbackIcon={'ShieldQuestion'}
                />
              )}
              <span className="truncate">
                {currentOption?.label ?? value?.toString() ?? placeholder ?? 'Unknown'}
              </span>
            </Inline>
          </DropdownButtonWithTooltip>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-72"
        onClick={(e) => e.stopPropagation()}
        withPortal
      >
        {subtitle && (
          <>
            <DropdownMenuLabel>{subtitle}</DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        {options
          .filter((option: DropdownOption) => option.value !== value)
          .map((option: DropdownOption) => {
            const actionIcon = option.icon;

            const DropdownMenuItemComponent = (
              <DropdownMenuItem
                disabled={disableItems || disabled}
                key={option.label}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(option);
                }}
              >
                {actionIcon && (
                  <ActionIcon
                    icon={actionIcon.icon}
                    variant={actionIcon.variant}
                    fallbackIcon={'ShieldQuestion'}
                  />
                )}
                <span className="text-ckds-neutral-900">{option.label}</span>
              </DropdownMenuItem>
            );

            if (disabled) {
              return (
                <Tooltip key={option.label} delayDuration={0}>
                  <TooltipTrigger asChild disabled={disabled}>
                    <div>{DropdownMenuItemComponent}</div>
                  </TooltipTrigger>
                  <TooltipContent>{tooltipContent}</TooltipContent>
                </Tooltip>
              );
            }
            return DropdownMenuItemComponent;
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
